import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from '../../../domain/service/payment/payment.service';
import { Payment } from '../../../domain/model/payment/payment.model';
import { NativeIoService } from '../../../core/native/native-io.service';

@Component({
  selector: 'app-payment-confirmation',
  templateUrl: './payment-confirmation.component.html',
  styleUrls: ['./payment-confirmation.component.css']
})
export class PaymentConfirmationComponent implements OnInit {
  /** Hosted-checkout completion: 3s between retries, ~5 tries while the gateway is still settling. */
  private static readonly CHECKOUT_RETRY_MS = 3000;
  private static readonly MAX_CHECKOUT_ATTEMPTS = 5;
  private static readonly CHECKOUT_SESSION_KEY = 'tdt.checkout';

  payment: Payment | null = null;
  loading = true;
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private paymentService: PaymentService,
    private nativeIo: NativeIoService
  ) { }

  ngOnInit(): void {
    const confirmationNumber = this.route.snapshot.paramMap.get('confirmationNumber');

    if (confirmationNumber) {
      this.clearBasket();
      this.loadPayment(confirmationNumber);
      return;
    }

    // Hosted checkout running in a child window: hand the outcome to the page that opened it
    // (make-payment settles via checkout/complete) and close; nothing renders in the popup.
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ tdtCheckout: 'returned' }, window.location.origin);
      window.close();
      return;
    }

    // No confirmation number: this may be a return from hosted checkout (?ref=&transactionId=,
    // falling back to the sessionStorage snapshot saved before the redirect if the gateway
    // dropped the query params). Neither source present → unchanged historical behavior.
    const { transactionId, ref } = this.resolveCheckoutParams();
    if (!transactionId) {
      this.router.navigate(['/app/payments']);
      return;
    }

    // The basket is cleared on success, not here: a checkout that ends up cancelled or failed
    // must leave the taxpayer's selection intact so they can retry.
    this.completeCheckout(transactionId, ref, 1);
  }

  /** Empty the session basket so paid returns don't linger once a payment is confirmed. */
  private clearBasket(): void {
    try {
      sessionStorage.removeItem('tdt.web.basket');
    } catch {
      /* ignore */
    }
  }

  private resolveCheckoutParams(): { transactionId: string | null; ref: number | null } {
    const qp = this.route.snapshot.queryParamMap;
    let transactionId = qp.get('transactionId');
    let ref = qp.get('ref');

    if (!transactionId) {
      try {
        const raw = sessionStorage.getItem(PaymentConfirmationComponent.CHECKOUT_SESSION_KEY);
        const saved = raw ? JSON.parse(raw) : null;
        transactionId = transactionId ?? saved?.transactionId ?? null;
        ref = ref ?? (saved?.ref != null ? String(saved.ref) : null);
      } catch {
        /* ignore malformed sessionStorage payload */
      }
    }

    return { transactionId, ref: ref != null ? Number(ref) : null };
  }

  /** Finalize a hosted-checkout payment; retries while the gateway is still settling the transaction. */
  private completeCheckout(transactionId: string, ref: number | null, attempt: number): void {
    this.loading = true;
    this.error = null;

    this.paymentService.completeCheckout(transactionId, ref).subscribe({
      next: (confirmation) => {
        this.clearBasket();
        try {
          sessionStorage.removeItem(PaymentConfirmationComponent.CHECKOUT_SESSION_KEY);
        } catch {
          /* ignore */
        }
        // Load the full Payment (id, status, receipt-ready fields) through the existing lookup
        // so the rest of this view — and receipt download/email, which need payment.id — works
        // exactly as it does for a historical confirmation.
        this.loadPayment(confirmation.confirmationNumber);
      },
      error: (err) => {
        const message: string = err?.error?.message || err?.error?.error || '';
        if (message.toLowerCase().includes('still processing')
            && attempt < PaymentConfirmationComponent.MAX_CHECKOUT_ATTEMPTS) {
          setTimeout(
            () => this.completeCheckout(transactionId, ref, attempt + 1),
            PaymentConfirmationComponent.CHECKOUT_RETRY_MS
          );
          return;
        }
        console.error('Error completing checkout:', err);
        this.error = message || 'Payment could not be confirmed. Please contact support.';
        this.loading = false;
      }
    });
  }

  get isCompleted(): boolean { return String(this.payment?.status ?? '').toUpperCase() === 'COMPLETED'; }
  get isPending(): boolean { return ['PENDING', 'PENDING_GATEWAY', 'PROCESSING'].includes(String(this.payment?.status ?? '').toUpperCase()); }
  get statusTitle(): string {
    const s = String(this.payment?.status ?? '').toUpperCase();
    if (this.isPending) return 'Payment Processing';
    if (s === 'REFUNDED' || s === 'REVERSED') return 'Payment Reversed';
    return s === 'FAILED' ? 'Payment Failed' : 'Payment Cancelled';
  }
  get statusMessage(): string {
    if (this.isPending) return 'Your payment is still being processed. Check your payment history shortly.';
    const s = String(this.payment?.status ?? '').toUpperCase();
    if (s === 'REFUNDED' || s === 'REVERSED') return 'This payment was reversed by the tax office.';
    return 'This payment was not completed. If you were charged, please contact the tax office.';
  }

  loadPayment(confirmationNumber: string): void {
    this.loading = true;
    this.error = null;

    this.paymentService.getPaymentByConfirmation(confirmationNumber).subscribe({
      next: (payment) => {
        this.payment = payment;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading payment:', err);
        this.error = 'Payment not found. Please check the confirmation number and try again.';
        this.loading = false;
      }
    });
  }

  downloadReceipt(): void {
    if (!this.payment) return;

    this.nativeIo.saveDownload(
      `receipt-${this.payment.confirmationNumber}.pdf`,
      this.paymentService.receiptUrl(this.payment.id),
      this.paymentService.downloadReceipt(this.payment.id),
    ).catch((err) => console.error('Error downloading receipt:', err));
  }

  emailReceipt(): void {
    if (!this.payment) return;

    // You could show a dialog to get email address
    const email = prompt('Enter email address:');
    if (!email) return;

    this.paymentService.emailReceipt(this.payment.id, email).subscribe({
      next: () => {
        alert('Receipt sent successfully!');
      },
      error: (err) => {
        console.error('Error emailing receipt:', err);
        alert('Failed to send receipt. Please try again.');
      }
    });
  }

  /** No print dialog in a WebView — the template hides the button there; Download shares the PDF. */
  get canPrint(): boolean {
    return !this.nativeIo.isNative;
  }

  print(): void {
    window.print();
  }

  goToPayments(): void {
    this.router.navigate(['/app/payments']);
  }

  // --- Money-flow breakdown aggregated across all returns on this payment ---
  private sumBy(pick: (r: any) => number | undefined): number {
    return (this.payment?.returns ?? []).reduce((s, r) => s + (pick(r) ?? 0), 0);
  }
  get sumTaxableReceipts(): number { return this.sumBy(r => r.taxableRentalReceipts); }
  get sumTaxes(): number { return this.sumBy(r => r.totalTaxes); }
  get sumLateFee(): number { return this.sumBy(r => r.lateFee); }
  get sumLateInterest(): number { return this.sumBy(r => r.lateInterest); }
  get sumAllowance(): number { return this.sumBy(r => r.onlineCollectionAllowance); }

  getPaymentMethodDisplay(): string {
    if (!this.payment?.paymentMethod) return '';

    const method = this.payment.paymentMethod;

    if (method.type === 'ACH') {
      return `Bank Account ending in ${method.accountNumberLast4}`;
    } else {
      return `${method.cardType} ending in ${method.cardLastFour}`;
    }
  }
}
